import { openPendingLink } from "../ui/link-join.js";
import { initSessionResume } from "../ui/session-resume.js";
import { placeBelowHeader } from "../ui/banner-position.js";
import { reloadApp } from "../../../engine/storage/index.js";
import { syncWithAccount, t } from "../../../shared/i18n/index.js";
/**
 * Auth Manager
 * Handles authentication flow, login/logout, and auth-related UI.
 * Extracted from main.js for better separation of concerns.
 *
 * NOTE: User state (currentUser) is managed by app.js.
 * This module manages masterKey and orchestrates auth flow.
 */

import { showScreen, hideAuthError } from "../ui/auth/screens.js";
import * as AuthForms from "../ui/auth/forms.js";
import { emit, on, Events } from "../../../engine/core/events.js";
import { setSigningOut } from "../../../engine/sync/storage-health.js";
import * as LoadProgress from "../ui/load-progress.js";
import { onSessionMessage } from "../../../engine/core/cross-tab-session.js";
import * as AccessRequests from "../../../engine/sync/access-requests.js";


// Module state
let callbacks = null;
let masterKey = null;
const MOBILE_RECOVERY_STASH_KEYS = [
  "mobile-unsaved-note-edits",
  "mobile-unsaved-node-edits",
];

/**
 * Render a non-destructive in-page banner notifying the user that a
 * sibling tab has signed out. Idempotent — multiple signals only show
 * one banner. The receiver does NOT take any state-altering action; the
 * user clicks "Reload" (which then runs the normal load path that
 * notices the absent session) or dismisses the banner if they want this
 * tab to keep operating against its existing in-memory state until they
 * decide.
 */
function showSiblingLogoutBanner(messageKey = "account.siblingLogout.message") {
  const shown = document.getElementById("sibling-logout-banner");
  if (shown) {
    shown.querySelector(".sibling-logout-banner-message").textContent = t(messageKey);
    return;
  }

  const banner = document.createElement("div");
  banner.id = "sibling-logout-banner";
  banner.className = "sibling-logout-banner";
  banner.setAttribute("role", "status");
  banner.setAttribute("aria-live", "polite");

  const message = document.createElement("span");
  message.className = "sibling-logout-banner-message";
  message.textContent = t(messageKey);
  banner.appendChild(message);

  const reloadBtn = document.createElement("button");
  reloadBtn.type = "button";
  reloadBtn.className = "sibling-logout-banner-action";
  reloadBtn.textContent = t("account.siblingLogout.reload");
  reloadBtn.addEventListener("click", () => {
    reloadApp();
  });
  banner.appendChild(reloadBtn);

  const dismissBtn = document.createElement("button");
  dismissBtn.type = "button";
  dismissBtn.className = "sibling-logout-banner-dismiss";
  dismissBtn.setAttribute("aria-label", t("account.siblingLogout.dismiss"));
  dismissBtn.textContent = "\u00D7";
  dismissBtn.addEventListener("click", () => {
    banner.remove();
  });
  banner.appendChild(dismissBtn);

  document.body.appendChild(banner);
  placeBelowHeader(banner);
}

/**
 * Update the header UI to reflect logged-in vs logged-out state.
 * @param {Object|null} user - The current user, or null if logged out
 */
export function updateHeaderUI(user) {
  const userArea = document.getElementById("user-area");
  const loginBtn = document.getElementById("header-login-btn");
  const loggedInArea = document.getElementById("logged-in-area");
  const usernameDisplay = document.getElementById("username-display");
  const notificationsBtn = document.getElementById("btn-notifications");

  if (user) {
    if (userArea) userArea.classList.remove("hidden");
    if (loginBtn) loginBtn.classList.add("hidden");
    if (loggedInArea) loggedInArea.classList.remove("hidden");
    if (usernameDisplay) usernameDisplay.textContent = user.username;
    if (notificationsBtn) notificationsBtn.classList.remove("hidden");
  } else {
    if (userArea) userArea.classList.remove("hidden");
    if (loginBtn) loginBtn.classList.remove("hidden");
    if (loggedInArea) loggedInArea.classList.add("hidden");
    if (notificationsBtn) notificationsBtn.classList.add("hidden");
  }
}

/**
 * Initialize auth manager with callbacks.
 *
 * @param {Object} cbs - Callbacks object with dependencies:
 *   - setCurrentUser: (user) => void
 *   - getCurrentUser: () => User|null
 *   - initializeAppCore: () => Promise<void>
 *   - setupCRDTEventListeners: () => void
 *   - initVaultStorage: (options) => void
 *   - enableUserCRDT: (userId, masterKey, options) => Promise<Result>
 *   - VaultStorage: VaultStorage module
 *   - VaultUI: VaultUI module
 *   - initializeLoggedOutMode: ({ userId, masterKey, isLoggedOut }) => Promise<void>
 *   - initShareModule: () => Promise<void>
 *   - initInvitations: () => Promise<void>
 *   - destroyInvitations: () => void
 *   - disableUserCRDT: () => Promise<void>
 *   - getLastOpenedDoc: () => {docId, isShared}|null
 *   - setLastOpenedDoc: (docId, isShared) => void
 *   - getDocState: (docId) => Object|null
 *   - stopCRDTSync: () => Promise<void>
 *   - CRDTBridge: CRDTBridge module
 *   - Store: Store module
 *   - switchView: (viewName) => void
 *   - markSaved: () => void
 *   - clearDeltaSyncState: () => void
 *   - cleanupCRDTEventListeners: () => void
 *   - updateSyncStatus: (state) => void
 *   - ConnectionState: ConnectionState enum
 *   - showNotification: (message, type) => void
 *   - generateId: () => string
 *   - ROOT_COLOR: string
 *   - renderTree: () => void
 *   - AuthHandlers: AuthHandlers module
 *   - showDeletionModal: () => void
 *   - Keypair: Keypair module
 *   - getKeypairManager: () => KeypairManager instance
 *   - isDocumentLoading: () => boolean
 *   - setIsDocumentLoading: (value) => void
 *   - DocumentLoader: DocumentLoader module (handleDocOpen, handleDocDelete)
 */
export function init(cbs) {
  callbacks = cbs;
}

/**
 * Get the current master key.
 */
export function getMasterKey() {
  return masterKey;
}

/**
 * Called after successful backend authentication.
 * Orchestrates the entire post-login flow.
 */
export async function onAuthSuccess({ user, masterKey: key, e2eeEnvelope, isNewUser = false }) {
  console.log("[AuthManager] Authenticated:", user.username);
  callbacks.setCurrentUser(user);
  masterKey = key;
  // Every sign-in ends here: per-account states start afresh (the server
  // indicator's "Signed out" after an ended session, quota, storage info).
  emit(Events.AUTH_LOGIN, { user });

  // Hide "Login to sync" button - user is now authenticated
  const loginToSyncBtn = document.getElementById("login-to-sync-btn");
  if (loginToSyncBtn) {
    loginToSyncBtn.classList.add("hidden");
  }

  // Update header
  updateHeaderUI(user);

  // Ensure basic app setup is done (view containers, modal, etc.)
  await callbacks.initializeAppCore();

  // Show main app screen immediately so user isn't stuck on login screen
  // while background initialization (CRDT sync, document loading) completes
  showScreen("main-app");

  // The app shell is visible — swap the fullscreen boot splash for the slim
  // top bar that tracks vault sync + initial document load without blocking.
  LoadProgress.done();
  LoadProgress.beginBar(t("account.load.syncingVault"));
  LoadProgress.stage(0.05, t("account.load.syncingVault"));

  // Setup CRDT event listeners
  callbacks.setupCRDTEventListeners();

  // Initialize vault storage with dependencies
  callbacks.initVaultStorage({
    getMasterKey: () => masterKey,
    getTemplateData: () => ({
      id: callbacks.generateId(),
      name: t("account.newMap.name"),
      color: callbacks.ROOT_COLOR,
      children: [],
      notes: [],
    }),
  });

  // Initialize vault with backend storage and User CRDT sync
  try {
    // Enable User CRDT for vault index sync across devices
    const {
      synced: userCRDTSynced,
      hasData: userCRDTHasData,
    } = await callbacks.enableUserCRDT(user.user_id, masterKey, { isNewUser });

    console.log(
      `[AuthManager] User CRDT enabled: synced=${userCRDTSynced}, hasData=${userCRDTHasData}`,
    );
    LoadProgress.stage(0.2, t("account.load.vaultSynced"));

    console.log("[AuthManager] Backend vault initialized");

    // Opens asked from here on (the vault is about to show) are the
    // person's: the initial map below does not replace them.
    const opensBeforeVault = callbacks.DocumentLoader.opensAskedSoFar?.();

    // Re-initialize VaultUI with backend storage
    callbacks.VaultUI.init({
      storage: callbacks.VaultStorage,
      onDocOpen: async (docId) => {
        console.log("[AuthManager] Opening doc:", docId);
        await callbacks.DocumentLoader.handleDocOpen(docId);
      },
      onShareOpen: async (shareId, shareInfo) => {
        console.log("[AuthManager] Opening shared document:", shareId);
        // In document-level sharing, shared docs open the same as regular docs
        await callbacks.DocumentLoader.handleDocOpen(shareId);
      },
      onDocCreate: async (docId) => {
        console.log("[AuthManager] Doc created:", docId);
        await callbacks.DocumentLoader.handleDocOpen(docId);
      },
      onDocDelete: async (docId) => {
        console.log("[AuthManager] Doc deleted:", docId);
        await callbacks.DocumentLoader.handleDocDelete(docId);
      },
      // For export/import and account deletion
      getMasterKey: () => masterKey,
      onLogout: () => callbacks.AuthHandlers.logout(),
      getKeypairManager: () => callbacks.getKeypairManager?.(),
      Keypair: callbacks.Keypair || null,
    });

    // Initialize sharing modules early so keypair is available for shared map restore
    // Skip if CRDT sync failed entirely — the vault structure won't exist yet,
    // and writing to it would crash. Sharing will init on next successful login.
    if (userCRDTSynced || userCRDTHasData) {
      try {
        await callbacks.initShareModule();
        await callbacks.initInvitations();
        // Hydrate the "pending access requests" set so stubs rendered
        // during this session already know which ones are awaiting a
        // grant (pending icon vs plain no-access).
        if (callbacks.hydrateAccessRequests) {
          await callbacks.hydrateAccessRequests();
        }
        console.log("[AuthManager] Sharing modules initialized");
      } catch (err) {
        console.error("[AuthManager] Failed to initialize sharing modules:", err);
      }
    } else {
      console.warn("[AuthManager] Skipping share module init — CRDT not synced and no local data");
    }

    // Load user's initial map (shared or regular, with CRDT initialization),
    // unless the person opened one meanwhile
    await callbacks.DocumentLoader.loadInitialDoc({ since: opensBeforeVault });

    // A map's link opened before signing in, or with this page: checked,
    // asked, joined and opened (ui/link-join.js).
    await openPendingLink({ openMap: (docId) => callbacks.DocumentLoader.handleDocOpen(docId) })
      .catch((error) => console.error("[AuthManager] Joining by link failed:", error));

    callbacks.VaultUI.updateCurrentDocName();

    // Mark current state as saved baseline (CRDT handles real-time sync)
    callbacks.markSaved();

    // Proactively prompt legacy accounts (no recovery_verifier) to regenerate
    // their recovery phrase — /auth/recover rejects verifier-less accounts, so
    // this restores their ability to recover. No-op once a verifier exists.
    AuthForms.maybeShowRecoveryUpgradePrompt(e2eeEnvelope);

    // Language: a choice made on this device goes to the account; otherwise
    // the account's language is adopted here (one reload, state kept).
    const saveLocale = async (code) => callbacks.AuthHandlers?.api?.("/api/account/preferences", "PUT", { locale: code });
    if ((await syncWithAccount(user.locale, saveLocale)) === "reload") {
      await reloadApp();
    }
  } catch (err) {
    console.error("[AuthManager] Failed to initialize backend vault:", err);
    // Ensure loading flag is cleared on error
    callbacks.setIsDocumentLoading(false);
  } finally {
    // Close the vault/initial-doc progress bar on every path (the document
    // loader normally closes it; this covers early failures).
    LoadProgress.done();
    emit(Events.APP_STARTED, {});
  }
}

/**
 * Called after logout.
 * Cleans up all authenticated state.
 */
export async function onLogout(cacheScope) {
  console.log("[AuthManager] Logged out");
  clearMobileRecoveryStashes(cacheScope);
  emit(Events.AUTH_LOGOUT);

  // Stop CRDT sync. Connections may close with changes the server never
  // confirmed (signing out offline): the sign-out named them and the user
  // chose to sign out anyway (see storage-health.js, setSigningOut).
  setSigningOut(true);
  try {
    await callbacks.stopCRDTSync();

    // Clean up CRDT event listeners to prevent duplicates on re-login
    callbacks.cleanupCRDTEventListeners();

    // Clear delta sync state first (stops auto-save timer)
    callbacks.clearDeltaSyncState();

    // Finish online persistence teardown before deleting account caches.
    // The device-local vault remains alive across browser and native sign-out.
    await callbacks.disableUserCRDT();
  } finally {
    setSigningOut(false);
  }
  await callbacks.CRDTBridge.clearAccountIndexedDBCaches(cacheScope);
  // Files attached offline and not sent: the sign-out warning counted them.
  try {
    const { deleteAllWaitingFiles } = await import('../../../engine/services/waiting-attachments.js');
    await deleteAllWaitingFiles();
  } catch (error) {
    console.warn('[Auth] Files waiting to be sent were not removed:', error);
  }

  // Destroy share revocation module (clears processed revocations + stops cleanup timer)
  if (callbacks.CRDTBridge.destroyShareRevocation) {
    callbacks.CRDTBridge.destroyShareRevocation();
  } else if (callbacks.CRDTBridge.clearProcessedRevocations) {
    callbacks.CRDTBridge.clearProcessedRevocations();
  }

  // Linked-map access markers and pending access requests describe the
  // previous account's access; the next account must start clean.
  callbacks.CRDTBridge.resetLinkedDocState?.();
  AccessRequests.reset();

  // Stop invitations polling
  try {
    callbacks.destroyInvitations();
  } catch (err) {
    // Module might not be loaded
  }

  // Zero keypair secret key material
  try {
    if (callbacks.destroyShareModule) {
      callbacks.destroyShareModule();
    }
  } catch (err) {
    // Module might not be loaded
  }

  callbacks.setCurrentUser(null);
  // Zero master key bytes before releasing reference
  if (masterKey instanceof Uint8Array) {
    masterKey.fill(0);
  }
  masterKey = null;

  // Clear backend vault
  if (callbacks.VaultStorage && callbacks.VaultStorage.clear) {
    callbacks.VaultStorage.clear();
  }

  // Reopen the device-local workspace, creating a starter map only if empty
  await resetToDefaultMap();

  updateHeaderUI(null);
  callbacks.updateSyncStatus(callbacks.ConnectionState.DISCONNECTED);

  // Show the local workspace
  showScreen("main-app");
  // The signed-out start's end: another interface starts again on it
  // (src/interfaces/index.js).
  emit(Events.APP_STARTED, {});
}

function clearMobileRecoveryStashes({ docIds = [] } = {}) {
  const accountDocs = new Set(docIds);
  for (const key of MOBILE_RECOVERY_STASH_KEYS) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const entries = JSON.parse(raw);
      if (!Array.isArray(entries)) continue;
      const kept = entries.filter(entry => !accountDocs.has(entry?.docId));
      if (kept.length === entries.length) continue;
      if (kept.length) localStorage.setItem(key, JSON.stringify(kept));
      else localStorage.removeItem(key);
    } catch (err) {
      // Storage can be unavailable in private contexts; logout should continue.
    }
  }
}

/**
 * Reopen the device-local workspace for logged-out users.
 * Uses CRDT-based logged-out mode for consistency with the rest of the app.
 */
export async function resetToDefaultMap() {
  // Get or create local user and master key for logged-out mode
  const localUser = callbacks.AuthHandlers.getOrCreateLocalUser();
  const localMasterKey = callbacks.AuthHandlers.getOrCreateLocalMasterKey();

  // Initialize logged-out mode - this handles all the CRDT setup, VaultUI init, etc.
  // The initializeLoggedOutMode function in main.js handles:
  // - VaultStorage initialization with logged-out config
  // - User CRDT enabling without sync
  // - Creating default map if needed
  // - VaultUI initialization with proper handlers
  // - Store initialization with tree data
  await callbacks.initializeLoggedOutMode({
    userId: localUser.user_id,
    masterKey: localMasterKey,
    isLoggedOut: true,
  });

  console.log("[AuthManager] Reset to default map for logged-out state (CRDT mode)");
}

/**
 * Setup auth-related DOM event handlers.
 */
export function setupAuthHandlers() {
  // Initialize auth handlers with callbacks for backend mode
  callbacks.AuthHandlers.init({
    onAuthSuccess: onAuthSuccess,
    onLogout: onLogout,
  });

  // Cross-tab session signals: only react to messages by showing a
  // non-destructive UI banner. Never wipe state, clear masterKey, or
  // touch IndexedDB on a sibling-tab signal — a hostile in-page script
  // could forge it. The user must take an explicit action to log out
  // here too.
  onSessionMessage((msg) => {
    if (msg.type === "logout") {
      // Only show if we're currently authenticated. Logged-out tabs and
      // freshly-loaded tabs that haven't completed login don't need a
      // confusing "session changed" nudge.
      if (callbacks.getCurrentUser?.()) {
        showSiblingLogoutBanner();
      }
    }
  });
  // This device's session ended (the server closed a socket with 4401:
  // signed out everywhere, password changed; or refuses the session when it
  // reconnects: engine/sync/session-check.js): sign in again in place, nothing
  // reloaded or deleted (interfaces/classic/ui/session-resume.js). Only sockets of a signed-in
  // page announce it; an API 401 may already have cleared currentUser.
  initSessionResume({
    isSignedIn: () => !!(callbacks.getCurrentUser?.() || callbacks.AuthHandlers?.getCachedUserId?.()),
    isSigningOutHere: () => !!callbacks.AuthHandlers?.isSigningOutHere?.(),
  });

  // Navigation between auth screens
  const showSignupBtn = document.getElementById("show-signup");
  const showLoginBtn = document.getElementById("show-login");
  const showRecoverBtn = document.getElementById("show-recover");
  const showLoginFromRecoverBtn = document.getElementById(
    "show-login-from-recover",
  );

  if (showSignupBtn) {
    showSignupBtn.addEventListener("click", (e) => {
      e.preventDefault();
      hideAuthError("login-error");
      showScreen("signup-screen");
    });
  }

  if (showLoginBtn) {
    showLoginBtn.addEventListener("click", (e) => {
      e.preventDefault();
      hideAuthError("signup-error");
      showScreen("login-screen");
    });
  }

  if (showRecoverBtn) {
    showRecoverBtn.addEventListener("click", (e) => {
      e.preventDefault();
      hideAuthError("login-error");
      showScreen("recover-screen");
    });
  }

  if (showLoginFromRecoverBtn) {
    showLoginFromRecoverBtn.addEventListener("click", (e) => {
      e.preventDefault();
      hideAuthError("recover-error");
      showScreen("login-screen");
    });
  }

  // Header login button
  const headerLoginBtn = document.getElementById("header-login-btn");
  if (headerLoginBtn) {
    headerLoginBtn.addEventListener("click", () => {
      showScreen("login-screen");
    });
  }

  // Header logout button
  const headerLogoutBtn = document.getElementById("header-logout-btn");
  if (headerLogoutBtn) {
    headerLogoutBtn.addEventListener("click", async () => {
      await callbacks.AuthHandlers.logout();
    });
  }

  // Notifications button
  const notificationsBtn = document.getElementById("btn-notifications");
  if (notificationsBtn) {
    notificationsBtn.addEventListener("click", async () => {
      await callbacks.showInvitationsModal();
    });
  }

  // Auth form handlers - connect to backend
  const loginForm = document.getElementById("login-form");
  if (loginForm) {
    loginForm.addEventListener("submit", AuthForms.handleLogin);
  }

  const registerForm = document.getElementById("register-form");
  if (registerForm) {
    registerForm.addEventListener("submit", AuthForms.handleRegister);
  }

  const recoverForm = document.getElementById("recover-form");
  if (recoverForm) {
    recoverForm.addEventListener("submit", AuthForms.handleRecover);
  }

  console.log("[AuthManager] Auth handlers initialized");
}
